import { createContext, ReactNode, useState, useEffect } from 'react'
import * as Sentry from '@sentry/nextjs'

type LocationContextType = {
  latitude?: number
  longitude?: number
  browserLocationGranted: boolean
  requestBrowserLocation: () => void
  setLocation: (lat: number, lon: number) => void
}

export const LocationContext = createContext<LocationContextType>({
  latitude: undefined,
  longitude: undefined,
  browserLocationGranted: false,
  requestBrowserLocation: () => {
    throw new Error('requestLocation must be used within LocationContext')
  },
  setLocation: () => {
    throw new Error('setLocation must be used within LocationContext')
  }
})

export type LocationProviderProps = {
  children: ReactNode
}

/**
 * Use to provide the active TabContext to consumers. DefaultTab can be
 * supplied in order to set the tab initially rendered.
 */
const LocationProvider = ({ children }: LocationProviderProps) => {
  const [latitude, setLatitude] = useState<number | undefined>(undefined)
  const [longitude, setLongitude] = useState<number | undefined>(undefined)
  const [browserLocationGranted, setBrowserLocationGranted] =
    useState<boolean>(false)

  useEffect(() => {
    // Check if the browser supports geolocation and if permissions are granted
    // Safari has implemented permissions but geolocation is not necessarily
    // a permission we can check for. Rescue this to prevent it breaking anything
    try {
      if (
        navigator.permissions &&
        typeof navigator.permissions.query === 'function'
      ) {
        navigator.permissions
          .query({ name: 'geolocation' })
          .then(status => {
            if (status.state === 'granted') {
              setBrowserLocationGranted(true)
              requestBrowserLocation()
            }
            status.addEventListener('change', () => {
              setBrowserLocationGranted(status.state === 'granted')
            })
          })
          .catch(error => {
            Sentry.captureException(error)
          })
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  }, [])

  const requestBrowserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          setLatitude(position.coords.latitude)
          setLongitude(position.coords.longitude)
        },
        error => {
          console.error('Error getting location:', error)
        }
      )
    } else {
      console.error('Geolocation is not supported by this browser.')
    }
  }

  const setLocation = (lat: number, lon: number) => {
    setLatitude(lat)
    setLongitude(lon)
  }

  return (
    <LocationContext.Provider
      value={{
        latitude,
        longitude,
        requestBrowserLocation,
        browserLocationGranted,
        setLocation
      }}
    >
      {children}
    </LocationContext.Provider>
  )
}

export default LocationProvider
